import React from 'react';
import { X, FileVideo, FileImage, Plus, AlertTriangle, GripVertical } from 'lucide-react';
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getItemPlatformWarnings } from '../../utils/platformRequirements';
import { MediaItem } from '../../types/media';

interface MediaThumbnailGalleryProps {
  mediaItems: MediaItem[];
  selectedItemId: string;
  onSelectItem: (id: string) => void;
  onDeleteItem: (id: string) => void;
  onReorder: (reorderedItems: MediaItem[]) => void;
  onAddMore: () => void;
  selectedPlatforms: string[];
  disabled?: boolean;
}

// Individual sortable thumbnail item
function SortableThumbnail({
  item,
  index,
  isSelected,
  warning,
  disabled,
  onSelect,
  onDelete,
}: {
  item: MediaItem;
  index: number;
  isSelected: boolean;
  warning: { excluded: string[]; message: string; isBlocking: boolean } | null;
  disabled: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition, // Snappier drag
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.6 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const isVideo = item.type === 'video';
  const hasWarning = warning !== null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "relative flex-shrink-0 group p-2", // Increased padding to avoid clipping
        isSelected && "rounded-xl bg-primary/15",
        hasWarning && warning?.isBlocking && !isSelected && "rounded-xl bg-red-500/20",
        hasWarning && !warning?.isBlocking && !isSelected && "rounded-xl bg-yellow-500/10"
      )}
      onClick={() => !disabled && onSelect()}
    >
      {/* Thumbnail */}
      <div className={cn(
        "w-24 h-24 rounded-xl overflow-hidden bg-card border-2 transition-colors relative",
        isSelected
          ? "border-primary shadow-[0_0_12px_rgba(var(--primary-rgb,59,130,246),0.3)]"
          : hasWarning && warning?.isBlocking
            ? "border-red-600"
            : hasWarning
              ? "border-yellow-500/60"
              : "border-border hover:border-border"
      )}>
        {/* Always use a static image for thumbnails -- never render <video> here */}
        {(item.thumbnailUrl || (!isVideo && item.preview)) ? (
          <img
            src={item.thumbnailUrl || item.preview || ''}
            alt={`Media ${index + 1}`}
            className="w-full h-full object-cover pointer-events-none"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center pointer-events-none">
            {isVideo ? (
              <FileVideo className="w-8 h-8 text-muted-foreground" />
            ) : (
              <FileImage className="w-8 h-8 text-muted-foreground" />
            )}
          </div>
        )}

        {/* Red overlay for items with blocking issues */}
        {hasWarning && (
          <div className={cn(
            "absolute inset-0 flex items-center justify-center",
            warning?.isBlocking ? "bg-red-900/50" : "bg-yellow-900/40"
          )}>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center",
                    warning?.isBlocking ? "bg-red-600" : "bg-yellow-600"
                  )}>
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs">
                  {warning!.message}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        {/* Type indicator */}
        <div className="absolute bottom-1 left-1">
          <Badge
            variant="secondary"
            className="text-[10px] px-1.5 py-0 h-5 bg-black/80 backdrop-blur-sm"
          >
            {isVideo ? (
              <><FileVideo className="w-3 h-3 mr-0.5" /> Video</>
            ) : (
              <><FileImage className="w-3 h-3 mr-0.5" /> Image</>
            )}
          </Badge>
        </div>
      </div>

      {/* Grip hint icon (visual only now) */}
      {!disabled && (
        <div
          className="absolute top-1 left-1 w-5 h-5 rounded bg-black/70 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="w-3 h-3 text-white" />
        </div>
      )}

      {/* Order number */}
      <div className="absolute bottom-1 right-1">
        <div className="w-5 h-5 rounded-full bg-black/80 backdrop-blur-sm flex items-center justify-center text-[10px] font-bold text-white">
          {index + 1}
        </div>
      </div>

      {/* Delete button */}
      {!disabled && (
        <Button
          variant="destructive"
          size="sm"
          className="absolute -top-2 -right-2 w-6 h-6 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

export default function MediaThumbnailGallery({
  mediaItems,
  selectedItemId,
  onSelectItem,
  onDeleteItem,
  onReorder,
  onAddMore,
  selectedPlatforms,
  disabled = false,
}: MediaThumbnailGalleryProps) {
  // Hooks must be called before any early returns
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Allow for 5px of movement before starting drag, helps distinguish clicks
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (mediaItems.length <= 1) {
    return null;
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = mediaItems.findIndex(item => item.id === active.id);
    const newIndex = mediaItems.findIndex(item => item.id === over.id);

    const reordered = arrayMove(mediaItems, oldIndex, newIndex).map((item, idx) => ({
      ...item,
      order: idx,
    }));

    onReorder(reordered);
  };

  // Pre-compute warnings for all items (path/fileName for Bluesky GIF detection)
  const allItemTypes = mediaItems.map(item => ({
    type: item.type,
    path: item.path ?? undefined,
    fileName: item.localFile?.name ?? undefined,
  }));

  return (
    <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Media Items ({mediaItems.length})
        </span>
        <Badge variant="secondary" className="text-xs">
          Drag to reorder
        </Badge>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={mediaItems.map(item => item.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="flex gap-2 overflow-x-auto py-3 px-1 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900 items-center">
            {mediaItems.map((item, index) => {
              const warning = getItemPlatformWarnings(
                index,
                item.type,
                allItemTypes,
                selectedPlatforms
              );

              return (
                <SortableThumbnail
                  key={item.id}
                  item={item}
                  index={index}
                  isSelected={item.id === selectedItemId}
                  warning={warning}
                  disabled={disabled}
                  onSelect={() => onSelectItem(item.id)}
                  onDelete={() => onDeleteItem(item.id)}
                />
              );
            })}

            {/* Add more button */}
            {!disabled && (
              <button
                onClick={onAddMore}
                className="flex-shrink-0 w-24 h-24 rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 flex flex-col items-center justify-center gap-1 transition-all cursor-pointer"
              >
                <Plus className="w-6 h-6 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground font-medium">Add more</span>
              </button>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
